import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Zap, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetToken, setResetToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(true);
      // In dev mode, the token is returned directly
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
      }
      toast.success("Reset link sent if account exists");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F4F8] to-[#E6EFF8] flex items-center justify-center p-6">
      <Card className="w-full max-w-md bg-white border border-slate-200 shadow-lg rounded-xl animate-fade-in" data-testid="forgot-password-card">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-flex items-center gap-2 justify-center mb-4">
            <div className="w-8 h-8 rounded-md bg-[#003087] flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#003087]" style={{ fontFamily: "Public Sans" }}>BandBoost AI</span>
          </Link>
          <CardTitle className="text-2xl text-slate-900" style={{ fontFamily: "Public Sans" }}>Reset Password</CardTitle>
          <CardDescription className="text-slate-500">
            {sent ? "Check your email for a reset link" : "Enter your email to receive a password reset link"}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6 pt-2">
          {sent ? (
            <div className="text-center py-4 space-y-4">
              <CheckCircle className="w-12 h-12 text-[#007F3B] mx-auto" />
              <p className="text-sm text-slate-600">
                If an account exists with <strong>{email}</strong>, a reset link has been sent.
              </p>
              {resetToken && (
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Development mode — reset link:</p>
                  <Link
                    to={`/reset-password?token=${resetToken}`}
                    className="text-sm text-[#003087] font-medium hover:underline break-all"
                    data-testid="reset-link"
                  >
                    Click here to reset password
                  </Link>
                </div>
              )}
              <Link to="/login">
                <Button variant="outline" className="mt-4" data-testid="back-to-login-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email Address</Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@nhs.net"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-[#003087] text-white hover:bg-[#002366]"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <p className="text-center text-sm text-slate-500 mt-4">
                <Link to="/login" className="text-[#003087] font-medium hover:underline">
                  <ArrowLeft className="w-3 h-3 inline mr-1" />Back to Sign In
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
